<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';

import Button from 'next/button/Button.vue';
import Dialog from 'next/dialog/Dialog.vue';
import TestToolForm from './TestToolForm.vue';
import TestToolResult from './TestToolResult.vue';

const props = defineProps({
  botId: {
    type: Number,
    required: true,
  },
  toolSet: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const stack = ref([]);
const canDismiss = computed(() => stack.value.length === 0);

const isOpen = ref(false);
const dialogRef = ref(null);
const formRef = ref(null);
const tool = ref({});
const testResult = ref('');
const testHasError = ref(false);

const resetStates = () => {
  testResult.value = '';
  testHasError.value = false;
};

const handleTest = async () => {
  resetStates();

  if (!formRef.value) return;
  const { state, validate } = formRef.value;

  try {
    const isFormValid = await validate();
    if (!isFormValid) return;

    const { conversation, ...parameters } = state;

    const testToolOptions = {
      botId: props.botId,
      toolSetId: props.toolSet.id,
      tool_name: `${props.toolSet.name}_${tool.value.name}`,
      parameters,
      context: {
        conversation_id: conversation.id,
      },
    };
    const result = await store.dispatch(
      'wgptBotTools/testTool',
      testToolOptions
    );
    testResult.value = result;
  } catch (error) {
    testHasError.value = true;
    testResult.value =
      error?.message ||
      t('WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.API.ERROR_MESSAGE');
  }
};

const handleOpen = () => {
  isOpen.value = true;
};

const handleDismiss = () => {
  if (!canDismiss.value) return;
  isOpen.value = false;
  emit('close');
  resetStates();
};

const dismiss = () => {
  dialogRef.value.close();
  handleDismiss();
};

const open = toolData => {
  tool.value = toolData;
  handleOpen();
  nextTick(() => {
    dialogRef.value?.open();
  });
};

useEmitter('WGPT_DROPDOWN_TOGGLE', ({ id, state }) => {
  if (state) {
    stack.value = [...stack.value, id];
  } else {
    stack.value = stack.value.filter(item => item !== id);
  }
});

defineExpose({ open, dismiss });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.TITLE')"
    width="3xl"
    :cancel-button-label="t('WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.CANCEL')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    :can-dismiss="canDismiss"
    overflow-y-auto
    @close="handleDismiss"
  >
    <div v-if="isOpen" class="flex items-stretch gap-4">
      <div class="flex-1">
        <TestToolForm
          ref="formRef"
          :parameter-fields="tool.trigger?.input_mapping?.parameters"
        />
      </div>
      <div class="flex-1 overflow-hidden">
        <TestToolResult
          :result="testResult"
          :has-error="testHasError"
          @test="handleTest"
        />
      </div>
    </div>
    <div class="flex justify-end">
      <Button
        size="sm"
        color="blue"
        :label="t('WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.CANCEL')"
        variant="ghost"
        @click.prevent="dismiss"
      />
    </div>
  </Dialog>
</template>
